import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, 
  LogOut, 
  Plus, 
  Minus, 
  Save, 
  Loader2, 
  Users, 
  AlertCircle,
  CheckCircle,
  Shield
} from 'lucide-react';
import { useAdminAuth } from '../contexts/AdminAuthContext';

interface User {
  uid: string;
  email: string;
  credits: number;
  created_at: string;
  stripe_customer_id: string;
}

interface EditingUser {
  uid: string;
  newCredits: number;
  saving: boolean;
}

export function AdminPanel() {
  const navigate = useNavigate();
  const { isAdmin, logout, token, isLoading: authLoading } = useAdminAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingUsers, setEditingUsers] = useState<Map<string, EditingUser>>(new Map());
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Redirect if not logged in
  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate('/admin/login');
    }
  }, [isAdmin, authLoading, navigate]);

  // Load users
  const loadUsers = async (search?: string) => {
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      const url = search 
        ? `/api/admin/users?search=${encodeURIComponent(search)}`
        : '/api/admin/users';
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to load users');
      }

      const data = await response.json();
      setUsers(data.users || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    if (isAdmin && token) {
      loadUsers();
    }
  }, [isAdmin, token]);

  // Debounced search
  useEffect(() => {
    if (!isAdmin || !token) return;

    const timer = setTimeout(() => {
      loadUsers(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery, isAdmin, token]);

  // Start editing a user's credits
  const startEditing = (user: User) => {
    setEditingUsers(prev => {
      const newMap = new Map(prev);
      newMap.set(user.uid, {
        uid: user.uid,
        newCredits: user.credits,
        saving: false,
      });
      return newMap;
    });
  };

  // Update editing credits
  const updateEditingCredits = (uid: string, change: number) => {
    setEditingUsers(prev => {
      const newMap = new Map(prev);
      const editing = newMap.get(uid);
      if (editing) {
        editing.newCredits = Math.max(0, editing.newCredits + change);
        newMap.set(uid, editing);
      }
      return newMap;
    });
  };

  // Set editing credits directly
  const setEditingCredits = (uid: string, value: number) => {
    setEditingUsers(prev => {
      const newMap = new Map(prev);
      const editing = newMap.get(uid);
      if (editing) {
        editing.newCredits = Math.max(0, value);
        newMap.set(uid, editing);
      }
      return newMap;
    });
  };

  // Save credits
  const saveCredits = async (uid: string) => {
    const editing = editingUsers.get(uid);
    if (!editing || !token) return;

    // Set saving state
    setEditingUsers(prev => {
      const newMap = new Map(prev);
      const current = newMap.get(uid);
      if (current) {
        current.saving = true;
        newMap.set(uid, current);
      }
      return newMap;
    });

    try {
      const response = await fetch(`/api/admin/users/${uid}/credits`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          credits: editing.newCredits,
          reason: 'manual adjustment via admin panel',
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to update credits');
      }

      const data = await response.json();

      // Update local user data
      setUsers(prev => prev.map(u => 
        u.uid === uid ? { ...u, credits: data.new_credits } : u
      ));

      // Remove from editing
      setEditingUsers(prev => {
        const newMap = new Map(prev);
        newMap.delete(uid);
        return newMap;
      });

      // Show success message
      setSuccessMessage('Credits updated successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save credits');
      console.error('Error saving credits:', err);

      // Reset saving state
      setEditingUsers(prev => {
        const newMap = new Map(prev);
        const current = newMap.get(uid);
        if (current) {
          current.saving = false;
          newMap.set(uid, current);
        }
        return newMap;
      });
    }
  };

  // Cancel editing
  const cancelEditing = (uid: string) => {
    setEditingUsers(prev => {
      const newMap = new Map(prev);
      newMap.delete(uid);
      return newMap;
    });
  };

  // Handle logout
  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  // Format date
  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return 'N/A';
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-indigo-900 to-gray-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-indigo-400 animate-spin" />
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-indigo-900 to-gray-900">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/20 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Admin Panel</h1>
                <p className="text-sm text-gray-400">User Management</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-white transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Success Message */}
        <AnimatePresence>
          {successMessage && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="flex items-center gap-2 p-4 bg-green-500/10 border border-green-500/20 rounded-lg"
            >
              <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
              <p className="text-sm text-green-400">{successMessage}</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Error Message */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/20 rounded-lg"
          >
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <p className="text-sm text-red-400">{error}</p>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-400 hover:text-red-300"
            >
              ×
            </button>
          </motion.div>
        )}

        {/* Search and Stats */}
        <div className="glass-card p-4 space-y-4">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="flex items-center gap-2 text-gray-300">
              <Users className="w-5 h-5" />
              <span className="font-medium">{users.length} Users</span>
            </div>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by email..."
                className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all"
              />
            </div>
          </div>
        </div>

        {/* Users List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-indigo-400 animate-spin" />
          </div>
        ) : users.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <Users className="w-12 h-12 text-gray-500 mx-auto mb-4" />
            <p className="text-gray-400">
              {searchQuery ? 'No users found matching your search' : 'No users yet'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {users.map((user) => {
              const editing = editingUsers.get(user.uid);
              const isEditing = !!editing;
              const currentCredits = isEditing ? editing.newCredits : user.credits;
              const hasChanges = isEditing && editing.newCredits !== user.credits;

              return (
                <motion.div
                  key={user.uid}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="glass-card p-4 space-y-3"
                >
                  {/* User Email */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-gray-400 truncate">{user.email}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Joined {formatDate(user.created_at)}
                      </p>
                    </div>
                  </div>

                  {/* Credits Display and Controls */}
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-2xl font-bold text-white">
                        {currentCredits}
                        <span className="text-sm text-gray-400 ml-2">credits</span>
                      </p>
                      <p className="text-xs text-gray-500">
                        ≈ {(currentCredits / 2).toFixed(1)} minutes
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          if (!isEditing) startEditing(user);
                          updateEditingCredits(user.uid, -10);
                        }}
                        disabled={editing?.saving}
                        className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 rounded-lg text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Minus className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => {
                          if (!isEditing) startEditing(user);
                          updateEditingCredits(user.uid, 10);
                        }}
                        disabled={editing?.saving}
                        className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 rounded-lg text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Plus className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {/* Credit Input (when editing) */}
                  {isEditing && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                    >
                      <input
                        type="number"
                        value={currentCredits}
                        onChange={(e) => setEditingCredits(user.uid, parseInt(e.target.value) || 0)}
                        min="0"
                        step="1"
                        disabled={editing?.saving}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all disabled:opacity-50"
                      />
                    </motion.div>
                  )}

                  {/* Action Buttons */}
                  {isEditing && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="flex gap-2"
                    >
                      <button
                        onClick={() => saveCredits(user.uid)}
                        disabled={!hasChanges || editing?.saving}
                        className="flex-1 btn-gradient min-h-[44px] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {editing?.saving ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span>Saving...</span>
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4" />
                            <span>Save</span>
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => cancelEditing(user.uid)}
                        disabled={editing?.saving}
                        className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Cancel
                      </button>
                    </motion.div>
                  )}

                  {/* Edit Button (when not editing) */}
                  {!isEditing && (
                    <button
                      onClick={() => startEditing(user)}
                      className="w-full min-h-[44px] flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-white transition-colors"
                    >
                      Edit Credits
                    </button>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
